// src/hooks/useBanners.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BannerService } from '../admin/pages/banner/bannerService';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────────────────────
export const BANNER_KEYS = {
  all: ['banners'] as const,
  list: () => ['banners', 'list'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useBanners
// ─────────────────────────────────────────────────────────────────────────────
export const useBanners = () => {
  return useQuery({
    queryKey: BANNER_KEYS.list(),
    queryFn: () => BannerService.getBanners(),
    staleTime: 1000 * 60 * 5,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBannerMutations
// ─────────────────────────────────────────────────────────────────────────────
export const useBannerMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: BANNER_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({
      userId,
      imageFile,
    }: {
      userId: string;
      imageFile: File;
    }) => BannerService.createBanner(userId, imageFile),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({
      bannerId,
      imageFile,
    }: {
      bannerId: string;
      imageFile: File;
    }) => BannerService.updateBanner(bannerId, imageFile),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (bannerId: string) => BannerService.deleteBanner(bannerId),
    onSuccess: invalidateAll,
  });

  return { create, update, remove };
};